import { type ReactNode, useMemo, useId } from "react";
import { useFintechUiStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFintechUiComponents,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { RadioGroup, type RadioGroupItemProps } from "@tenant/ui";
import clsx from "clsx";

import type { FintechProductBulletProps } from "../FintechProductBullet";
import defaultStyles from "./FintechProductOptionsSection.module.scss";

export interface FintechProductOptionsSectionProps
  extends PlatformComponentProps {
  options: FintechProductOptionsSectionItemProps[];
  onSelect: (index: number) => void;
}

export interface FintechProductOptionsSectionItemProps
  extends PlatformComponentProps {
  selected: boolean;
  onClick: () => void;
  title: ReactNode;
  description?: ReactNode;
  selectedContent: FintechProductBulletProps;
}

export const FintechProductOptionsSection = ({
  options,
  onSelect,
  className,
}: FintechProductOptionsSectionProps) => {
  const [block, cn] = useModuleBEM(
    useFintechUiStyles(
      CoreFintechUiComponents.FintechProductOptionsSection,
      defaultStyles
    ),
    CoreFintechUiComponents.FintechProductOptionsSection
  );

  const { t } = useI18nContext();

  const radioGroupId = useId();

  const selectedIndex = useMemo(
    () => options.findIndex((item) => item.selected),
    [options]
  );

  const radioGroupOptions: RadioGroupItemProps[] = useMemo(
    () =>
      options.map((item, index) => {
        return {
          key: `${radioGroupId}-${index}`,
          value: String(index),
          label: (
            <div
              aria-label={
                typeof item.title === "string"
                  ? item.title
                  : t("fintechProduct")
              }
            >
              <div className={cn("option-content")}>
                <span className={cn("option-title")}>{item.title}</span>
                {item.description == null ? null : (
                  <span className={cn("option-description")}>
                    {item.description}
                  </span>
                )}
              </div>
            </div>
          ),
        } satisfies RadioGroupItemProps;
      }),
    [cn, radioGroupId, options, t]
  );

  return (
    <div className={clsx(block, className)}>
      <RadioGroup
        options={radioGroupOptions}
        className={cn("radio-group")}
        classNames={{
          itemContainer: clsx(className, cn("option-container")),
        }}
        selectedOption={String(selectedIndex)}
        onSelectValue={(value) => {
          const index = Number(value);
          options[index].onClick();
          onSelect(index);
        }}
      />
    </div>
  );
};
